/**
 * Alertas do Monitoramento de Dispositivos.
 *
 * Regras executadas no backend (regra 38): dispositivo sem
 * monitoramento, monitoramento atrasado, aumento anormal de
 * consumo/captura, recorrência, aumento de espécie, área com
 * tendência crescente, danificado, extraviado, substituído.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "./hierarchy";

const RULE_SEVERITY = {
  device_missing: "red",
  device_damaged: "red",
  late_monitoring: "amber",
  schedule_not_completed: "amber",
  no_monitoring: "amber",
  variation_increase: "purple",
  capture_increase: "purple",
  species_increase: "purple",
  recurrent_capture: "amber",
  recurrent_consumption: "amber",
  area_trend: "orange",
  high_species_activity: "orange",
  device_replaced: "slate",
};

const SEVERITY_STYLE = {
  red: "border-red-500/40 bg-red-500/10",
  amber: "border-amber-500/40 bg-amber-500/10",
  orange: "border-orange-500/40 bg-orange-500/10",
  purple: "border-purple-500/40 bg-purple-500/10",
  slate: "border-slate-500/40 bg-slate-500/10",
};

const SEVERITY_ICON = {
  red: "text-red-500",
  amber: "text-amber-500",
  orange: "text-orange-500",
  purple: "text-purple-500",
  slate: "text-slate-400",
};

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "ABERTO", label: "Abertos" },
  { value: "TRATADO", label: "Tratados" },
];

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("ABERTO");
  const [rule, setRule] = useState("");
  const [loading, setLoading] = useState(true);
  const [ackId, setAckId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status) params.status = status;
      if (rule) params.rule = rule;
      const r = await api.get("/device-monitoring/alerts", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setRules(r.data.rules || []);
    } catch (err) {
      setItems([]);
      toast.error(apiErrorMessage(err, "Erro ao carregar alertas."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, rule]);

  const acknowledge = async (id) => {
    setAckId(id);
    try {
      await api.post(`/device-monitoring/alerts/${id}/acknowledge`);
      setItems((rows) =>
        rows.map((a) =>
          a.id === id ? { ...a, status: "TRATADO" } : a
        )
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao tratar alerta."));
    } finally {
      setAckId(null);
    }
  };

  const ruleLabel = (key) => {
    const found = rules.find((r) => r.key === key);
    return found?.label || key?.replace(/_/g, " ");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="font-semibold">
          Alertas operacionais ({total})
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            value={rule}
            onChange={(e) => setRule(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
          >
            <option value="">Todas as regras</option>
            {rules.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Nenhum alerta para os filtros selecionados.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => {
            const severity =
              RULE_SEVERITY[a.rule] || "slate";
            return (
              <div
                key={a.id}
                className={`rounded-lg border p-4 ${SEVERITY_STYLE[severity]}`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className={`h-5 w-5 mt-0.5 ${SEVERITY_ICON[severity]}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {ruleLabel(a.rule)}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          a.status === "ABERTO"
                            ? "bg-amber-500/20 text-amber-600"
                            : "bg-emerald-500/20 text-emerald-600"
                        }`}
                      >
                        {a.status === "ABERTO" ? "Aberto" : "Tratado"}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{a.message}</p>
                    <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-4">
                      {a.device_code && (
                        <span>Dispositivo: {a.device_code}</span>
                      )}
                      {a.area_name && <span>Área: {a.area_name}</span>}
                      {a.unit_name && <span>Unidade: {a.unit_name}</span>}
                      {a.created_at && (
                        <span>
                          {a.created_at.slice(0, 16).replace("T", " ")}
                        </span>
                      )}
                    </div>
                  </div>
                  {a.status === "ABERTO" && (
                    <button
                      onClick={() => acknowledge(a.id)}
                      disabled={ackId === a.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-card text-sm disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {ackId === a.id ? "Tratando..." : "Tratar"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
